import { CreditCard, Landmark, ListChecks, Undo2, UserRound, Wallet } from 'lucide-react';
import { RealScreen } from '@/components/marketing/roleKit';

/**
 * Payments Real Screen, used by parents and tutors (`audience`). Amounts are example data.
 * Facts: one payment per session; students (or parents with permission) pay by card through Stripe;
 * tutors see the session's payment and their share (payouts are not automated by the app).
 */
type PayAudience = 'parents' | 'tutors';

const STATUS_CLASS: Record<string, string> = {
  Paid: 'bg-emerald-600 text-white',
  'Due now': 'bg-orange-600 text-white',
  Scheduled: 'bg-muted text-foreground',
};

function Table({ rows, amountLabel }: { rows: readonly (readonly [string, string, string, string])[]; amountLabel: string }) {
  return (
    <div className="overflow-hidden rounded-xl border bg-background">
      <div className="grid grid-cols-[1fr_auto_auto] gap-x-4 border-b bg-muted/40 px-4 py-2 text-xs font-semibold text-muted-foreground">
        <span>Session</span>
        <span className="text-right">{amountLabel}</span>
        <span className="w-20 text-right">Status</span>
      </div>
      <ul className="divide-y">
        {rows.map(([subject, date, amount, status]) => (
          <li key={subject + date} className="grid grid-cols-[1fr_auto_auto] items-center gap-x-4 px-4 py-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">{subject}</p>
              <p className="text-xs text-muted-foreground">{date}</p>
            </div>
            <span className="text-sm font-semibold tabular-nums">{amount}</span>
            <span className="flex w-20 justify-end">
              <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS_CLASS[status]}`}>{status}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

const PARENT_ROWS = [
  ['Algebra', 'Wed', '$50.00', 'Due now'],
  ['SAT Math', 'Last Fri', '$50.00', 'Paid'],
  ['Algebra', 'Last Wed', '$50.00', 'Paid'],
  ['SAT Math', 'Next Fri', '$50.00', 'Scheduled'],
] as const;

const TUTOR_ROWS = [
  ['Alex M. · Algebra', 'Wed', '$35.00', 'Due now'],
  ['Jamie L. · SAT Math', 'Last Fri', '$35.00', 'Paid'],
  ['Alex M. · Algebra', 'Last Wed', '$35.00', 'Paid'],
  ['Ryan K. · Physics', 'Next Fri', '$35.00', 'Scheduled'],
] as const;

export function PaymentsFeatureSection({ audience }: { audience: PayAudience }) {
  if (audience === 'parents') {
    return (
      <RealScreen
        id="section-payments"
        audience="parents"
        layout="band"
        heading="Pay once the session has happened"
        lede="Each session has one payment. You see the amount before you pay, and the history stays in one place."
        callouts={[
          { icon: CreditCard, title: 'Card payments through Stripe', body: 'You pay by card once the session is complete.' },
          { icon: Undo2, title: 'Cancelled sessions are not charged', body: 'Cancel before a session happens and its payment is cancelled with it.' },
          { icon: UserRound, title: 'Pay for your student', body: 'When your student allows it, you can book and pay on their behalf.' },
        ]}
        caption="Example payment history. Amounts are illustrative."
      >
        <Table rows={PARENT_ROWS} amountLabel="Amount" />
      </RealScreen>
    );
  }
  return (
    <RealScreen
      id="section-payments"
      audience="tutors"
      layout="below"
      heading="Every session, with your share"
      lede="Students pay through Mentara. Your payments page lists each session’s payment, its status, and what you earn from it."
      callouts={[
        { icon: Wallet, title: 'Your share on each line', body: 'See what a session earns you, not just what the student paid.' },
        { icon: ListChecks, title: 'A status for every payment', body: 'Pending, due, paid or refunded, so you know where each one stands.' },
        { icon: Landmark, title: 'Card handling stays with Stripe', body: 'Students pay by card through Stripe. Payouts are arranged with the Mentara team.' },
      ]}
      caption="Example tutor payments. Amounts are illustrative."
    >
      <div className="mx-auto max-w-3xl">
        <Table rows={TUTOR_ROWS} amountLabel="Your share" />
      </div>
    </RealScreen>
  );
}
